import { useEffect, useReducer } from "react";
import { useForm } from "react-hook-form";

import AssignmentApi from "@/api/assignment-api";
import CompetencyApi from "@/api/competency";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import ListInput from "@/components/common/input/list";
import Select from "@/components/common/input/select";
import Textarea from "@/components/common/input/textarea";
import { handled, logRejection } from "@/helpers";
import { showSnackbar } from "@/hooks/snackbar";
import type ApiError from "@/models/ApiError";
import type { Competency } from "@/types/competency";

interface AreaOption {
  id: string;
  name: string;
}

interface CompetencyFormState {
  positive_indicators: string[];
  negative_indicators: string[];
  questions_leader: string[];
  questions_ic: string[];
  is_evali: boolean;
  area_options: AreaOption[];
}

type CompetencyFormAction =
  | { type: "add-positive-indicator"; payload: string[] }
  | { type: "add-negative-indicator"; payload: string[] }
  | { type: "add-question-leader"; payload: string[] }
  | { type: "add-question-ic"; payload: string[] }
  | { type: "is-evali"; payload: boolean }
  | { type: "add-area-option"; payload: AreaOption };

// The list fields are not registered with react-hook-form, they come off the reducer
// state at submit time.
interface CompetencyFormValues {
  name: string;
  area_slug: string;
  description: string;
  positive_indicators: string[];
  negative_indicators: string[];
  questions_leader: string[];
  questions_ic: string[];
}

const initial_state: CompetencyFormState = {
  positive_indicators: [],
  negative_indicators: [],
  questions_leader: [],
  questions_ic: [],
  is_evali: false,
  area_options: [
    // also change backend when modifying
    { id: "business", name: "Business" },
    { id: "leadership", name: "Leadership" },
    { id: "tech", name: "Tech" },
  ],
};

const reducer = (state: CompetencyFormState, action: CompetencyFormAction): CompetencyFormState => {
  switch (action.type) {
    case "add-positive-indicator":
      return {
        ...state,
        positive_indicators: action.payload,
      };
    case "add-negative-indicator":
      return {
        ...state,
        negative_indicators: action.payload,
      };
    case "add-question-leader":
      return {
        ...state,
        questions_leader: action.payload,
      };
    case "add-question-ic":
      return {
        ...state,
        questions_ic: action.payload,
      };
    case "is-evali":
      return { ...state, is_evali: action.payload };
    case "add-area-option": {
      const new_options = state.area_options;
      new_options.push(action.payload);
      return { ...state, area_options: new_options };
    }
  }
};

// toggleFn arrives from the surrounding Modal through cloneElement, never from the call site.
// Without an id the form creates a competency, without an assignment_id it edits the base one.
interface CompetencyFormProps {
  id?: string;
  assignment_id?: string;
  toggleFn?: () => void;
  callbackFn?: (competency: Competency | null) => void;
}

export default function CompetencyForm({
  id,
  assignment_id,
  toggleFn,
  callbackFn,
}: CompetencyFormProps) {
  const [state, dispatch] = useReducer(reducer, initial_state);
  const { register, handleSubmit, reset } = useForm<CompetencyFormValues>();

  useEffect(() => {
    if (!id) {
      return;
    }
    const abort_controller = new AbortController();

    const fetchData = async () => {
      const base_data = await CompetencyApi.fetchOne(id, abort_controller.signal);
      // special case to lock down evali competencies
      if (base_data.data?.is_evali) {
        dispatch({ type: "is-evali", payload: true });
        dispatch({
          type: "add-area-option",
          payload: { id: base_data.data.area_slug, name: base_data.data.area },
        });
      }
      let override_data: Competency | null = null;
      if (assignment_id) {
        const assignment_specific_data = await AssignmentApi.getAssignmentCompetency(
          assignment_id,
          id,
          abort_controller.signal,
        );
        override_data = assignment_specific_data.data;
      }
      const combined_data: Record<string, any> = {
        ...base_data.data,
      };
      // check for overrides
      const overridable_keys = [
        "name",
        "description",
        "questions_leader",
        "questions_ic",
        "positive_indicators",
        "negative_indicators",
      ] as const;
      overridable_keys.forEach((key) => {
        if (override_data && key in override_data && override_data[key] !== null) {
          combined_data[key] = override_data[key];
        }
      });
      reset(combined_data);
      dispatch({
        type: "add-positive-indicator",
        payload: combined_data.positive_indicators,
      });
      dispatch({
        type: "add-negative-indicator",
        payload: combined_data.negative_indicators,
      });
      dispatch({
        type: "add-question-leader",
        payload: combined_data.questions_leader,
      });
      dispatch({
        type: "add-question-ic",
        payload: combined_data.questions_ic,
      });
    };
    fetchData().catch(logRejection);
    return () => {
      abort_controller.abort();
    };
  }, [id, assignment_id, reset]);

  // The modal stays open on a failure so the filled-in form is still there.
  const _reportSaveFailure = (error: ApiError) => {
    console.error(error);
    showSnackbar("Could not save the competency.", "warning");
  };

  const _handleSubmit = (form_data: CompetencyFormValues) => {
    const payload: CompetencyFormValues = {
      ...form_data,
      positive_indicators: state.positive_indicators,
      negative_indicators: state.negative_indicators,
      questions_leader: state.questions_leader,
      questions_ic: state.questions_ic,
    };
    if (assignment_id && id) {
      // these are overrides, save accordingly
      AssignmentApi.addCompetencyToAssignment(assignment_id, id, payload)
        .then(({ error, data }) => {
          if (error) return _reportSaveFailure(error);
          // to let parent component resume behind modal action
          if (callbackFn) callbackFn(data);
          toggleFn?.();
        })
        .catch(logRejection);
    } else {
      // this is a base competency edit
      CompetencyApi.save({ data: payload, competency_id: id ? id : null })
        .then(({ error, data }) => {
          if (error) return _reportSaveFailure(error);
          // to let parent component resume behind modal action
          if (callbackFn) callbackFn(data);
          toggleFn?.();
        })
        .catch(logRejection);
    }
  };

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    toggleFn?.();
  };

  return (
    <form
      onSubmit={handled(handleSubmit(_handleSubmit))}
      className="mt-4 w-[60rem] rounded-lg bg-slate-800 p-8"
    >
      {state.is_evali && (
        <div className="mb-4 rounded-md border-2 border-red-700 bg-red-300 p-4 text-red-700">
          This is an evali competency, modify with care!
        </div>
      )}
      <Input label="Name" id="name" register={register} />
      <Select label="Category" id="area_slug" register={register} disabled={!!assignment_id}>
        <option value="">Select a category</option>
        {state.area_options.map((ao) => {
          return (
            <option key={ao.id} value={ao.id}>
              {ao.name}
            </option>
          );
        })}
      </Select>
      <Textarea
        label="Description"
        id="description"
        register={register}
        disabled={state.is_evali}
      />
      <div className="grid grid-cols-2 gap-8">
        <ListInput
          items={state.positive_indicators}
          label="Positive indicators"
          name="positive_indicators"
          callbackFn={(new_values) =>
            dispatch({ type: "add-positive-indicator", payload: new_values })
          }
        />
        <ListInput
          items={state.negative_indicators}
          label="Negative indicators"
          name="negative_indicators"
          callbackFn={(new_values) =>
            dispatch({ type: "add-negative-indicator", payload: new_values })
          }
        />
        <ListInput
          items={state.questions_leader}
          label="Questions leader"
          name="questions_leader"
          callbackFn={(new_values) =>
            dispatch({ type: "add-question-leader", payload: new_values })
          }
        />
        <ListInput
          items={state.questions_ic}
          label="Questions IC"
          name="questions_ic"
          callbackFn={(new_values) => dispatch({ type: "add-question-ic", payload: new_values })}
        />
      </div>
      <div className="flex items-center gap-4">
        <Button type="submit">Submit</Button>
        <Button onClick={_handleCancel}>Cancel</Button>
      </div>
    </form>
  );
}
